/**
 * Country picker bottom-sheet for the dialer.
 *
 * Lists only callable destinations (rows the backend reports for
 * `/api/rates/countries`). Renders a flag emoji from the ISO alpha-2.
 */
import { useEffect, useMemo, useState } from "react";
import { X, Search } from "lucide-react";

export function flagFor(cc) {
  if (!cc || cc.length !== 2) return "🌍";
  try {
    return String.fromCodePoint(
      ...cc.toUpperCase().split("").map((c) => 0x1f1a5 + c.charCodeAt(0))
    );
  } catch { return "🌍"; }
}

export default function CountryPicker({ open, onClose, countries, onPick, currentCode }) {
  const [q, setQ] = useState("");

  useEffect(() => {
    if (!open) setQ("");
    if (open) {
      // Lock body scroll while open.
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => { document.body.style.overflow = prev; };
    }
  }, [open]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return countries;
    return countries.filter((c) =>
      c.country_name.toLowerCase().includes(needle) ||
      c.country_code.toLowerCase().includes(needle) ||
      c.prefix.includes(needle.replace("+", "")),
    );
  }, [q, countries]);

  if (!open) return null;
  return (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center sm:justify-center bg-black/50 backdrop-blur-sm"
      onClick={onClose}
      data-testid="country-picker-backdrop"
    >
      <div
        className="bg-white w-full sm:max-w-md sm:rounded-2xl rounded-t-2xl max-h-[80vh] flex flex-col shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="country-picker"
      >
        <div className="flex items-center justify-between border-b border-brand-line px-5 py-4">
          <h3 className="font-display font-black text-lg text-brand-ink">Select destination</h3>
          <button onClick={onClose} data-testid="country-picker-close"
            className="rounded-full p-1.5 hover:bg-brand-bgSoft">
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="px-5 py-3 border-b border-brand-line/60">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-muted" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Country or dialing code"
              data-testid="country-picker-search"
              className="w-full rounded-full bg-brand-bgSoft border border-transparent pl-9 pr-3 py-2.5 text-sm focus:bg-white focus:border-brand-green focus:outline-none"
            />
          </div>
        </div>

        <div className="overflow-y-auto" data-testid="country-picker-list">
          {filtered.length === 0 ? (
            <p className="px-5 py-8 text-center text-sm text-brand-muted">
              No callable destinations match “{q}”. As we add new rates they’ll appear here.
            </p>
          ) : (
            <ul className="divide-y divide-brand-line/60">
              {filtered.map((c) => {
                const active = c.country_code === currentCode;
                return (
                  <li key={c.country_code}>
                    <button
                      type="button"
                      onClick={() => { onPick(c); onClose(); }}
                      data-testid={`country-option-${c.country_code}`}
                      className={`w-full flex items-center gap-3 px-5 py-3 text-left transition-colors ${
                        active ? "bg-brand-greenSoft" : "hover:bg-brand-bgSoft"
                      }`}
                    >
                      <span className="text-2xl leading-none" aria-hidden>{flagFor(c.country_code)}</span>
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-sm text-brand-ink truncate">{c.country_name}</p>
                        <p className="text-xs text-brand-muted">from £{c.rate_per_minute.toFixed(3)}/min</p>
                      </div>
                      <span className="font-mono text-sm font-bold text-brand-greenDark">+{c.prefix}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
}
